import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, ChevronLeftIcon, SaveIcon, CheckIcon, AlertCircleIcon, InfoIcon, CreditCardIcon, BuildingIcon, UsersIcon, TagIcon, PlusIcon, XIcon, SearchIcon, WalletIcon, EditIcon, CalendarDaysIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
// Step definitions
const STEPS = [{
  id: 1,
  name: 'Basic Info',
  description: 'Campaign details & duration'
}, {
  id: 2,
  name: 'Targeting Rules',
  description: 'Audience & eligibility'
}, {
  id: 3,
  name: 'Discount Structure',
  description: 'Values & limits'
}, {
  id: 4,
  name: 'Funding Split',
  description: 'Cost sharing'
}, {
  id: 5,
  name: 'Budget',
  description: 'Budget allocation'
}, {
  id: 6,
  name: 'Review',
  description: 'Summary & confirmation'
}];
// Mock Data
const MOCK_MERCHANTS = [{
  id: 'M001',
  name: 'Bismillah Traders'
}, {
  id: 'M002',
  name: 'Karachi Super Mart'
}, {
  id: 'M003',
  name: 'Chase Value Center'
}, {
  id: 'M004',
  name: 'Orient Online Shop'
}, {
  id: 'M005',
  name: 'Cafe Khaas'
}, {
  id: 'M006',
  name: 'Zeen Textile'
}, {
  id: 'M007',
  name: 'Islamabad Metro'
}, {
  id: 'M008',
  name: 'Ginsoy Lahore'
}];
const MOCK_BANKS = ['HBL', 'UBL', 'MCB', 'Allied Bank', 'Bank Alfalah', 'Meezan Bank', 'Faysal Bank', 'JS Bank', 'Askari Bank', 'Standard Chartered'];
const MOCK_MCCS = [{
  code: '5411',
  description: 'Grocery Stores, Supermarkets'
}, {
  code: '5812',
  description: 'Eating Places, Restaurants'
}, {
  code: '5311',
  description: 'Department Stores'
}, {
  code: '5999',
  description: 'Miscellaneous & Specialty Retail'
}, {
  code: '5541',
  description: 'Service Stations (Gas)'
}, {
  code: '5691',
  description: 'Clothing Stores'
}, {
  code: '5732',
  description: 'Electronics Stores'
}, {
  code: '5912',
  description: 'Drug Stores, Pharmacies'
}, {
  code: '7011',
  description: 'Hotels, Motels, Resorts'
}, {
  code: '4814',
  description: 'Telecom Services'
}];
const WEEKDAYS = [{
  key: 'Mon',
  label: 'Monday'
}, {
  key: 'Tue',
  label: 'Tuesday'
}, {
  key: 'Wed',
  label: 'Wednesday'
}, {
  key: 'Thu',
  label: 'Thursday'
}, {
  key: 'Fri',
  label: 'Friday'
}, {
  key: 'Sat',
  label: 'Saturday'
}, {
  key: 'Sun',
  label: 'Sunday'
}];
const CampaignForm: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  // Form State
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    code: 'CMP-' + Math.floor(Math.random() * 10000),
    description: '',
    startDate: '',
    endDate: '',
    daySelectMode: 'all' as 'all' | 'specific',
    applicableDays: [] as string[],
    // Targeting
    transactionTypes: ['Card', 'RAAST QR'],
    schemes: ['Visa', 'MasterCard'],
    bankSelectMode: 'all' as 'all' | 'specific',
    banks: [] as string[],
    merchantSelectMode: 'all' as 'all' | 'specific',
    merchants: [] as string[],
    binSelectMode: 'all' as 'all' | 'specific',
    bins: [] as string[],
    mccSelectMode: 'all' as 'all' | 'specific',
    mccs: [] as string[],
    // Discount Structure
    discountType: 'percentage',
    discountValue: 0,
    maxCap: 0,
    minTransaction: 0,
    maxDiscountPerCustomer: 0,
    usageLimitPerCustomer: 1,
    usageCycle: 'monthly',
    // Funding Split
    split: {
      merchant: 40,
      institution: 30,
      scheme: 30,
      bank: 0
    },
    // Budget
    totalBudget: 0,
    dailyBudget: 0,
    monthlyBudget: 0
  });
  // UI State for inputs
  const [merchantSearch, setMerchantSearch] = useState('');
  const [bankSearch, setBankSearch] = useState('');
  const [binInput, setBinInput] = useState('');
  const [mccSearch, setMccSearch] = useState('');
  const [budgetLocked, setBudgetLocked] = useState(false);
  const [isRevisingBudget, setIsRevisingBudget] = useState(false);
  const [budgetRevisionReason, setBudgetRevisionReason] = useState('');
  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleSubmit = () => {
    // Submit logic here
    navigate('/campaigns');
  };
  // Helper functions
  const toggleSelection = (list: string[], item: string) => {
    return list.includes(item) ? list.filter(i => i !== item) : [...list, item];
  };
  const toggleDay = (day: string) => {
    setFormData(prev => ({
      ...prev,
      applicableDays: prev.applicableDays.includes(day) ? prev.applicableDays.filter(d => d !== day) : [...prev.applicableDays, day]
    }));
  };
  const selectWeekdaysOnly = () => {
    setFormData(prev => ({
      ...prev,
      applicableDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
    }));
  };
  const selectWeekendsOnly = () => {
    setFormData(prev => ({
      ...prev,
      applicableDays: ['Sat', 'Sun']
    }));
  };
  const selectAllDaysSpecific = () => {
    setFormData(prev => ({
      ...prev,
      applicableDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    }));
  };
  const addBin = () => {
    if (binInput.length >= 6 && binInput.length <= 8 && !formData.bins.includes(binInput)) {
      setFormData({
        ...formData,
        bins: [...formData.bins, binInput]
      });
      setBinInput('');
    }
  };
  const removeBin = (bin: string) => {
    setFormData({
      ...formData,
      bins: formData.bins.filter(b => b !== bin)
    });
  };
  // --- RENDER STEPS ---
  const renderStep1 = () => <div className="space-y-6" data-id="element-2591">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2592">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2593">
          <InfoIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2594" />
          Campaign Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2595">
          <div data-id="element-2596">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2597">
              Campaign Name <span className="text-red-500" data-id="element-2598">*</span>
            </label>
            <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="e.g. Summer Sale 2023" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} data-id="element-2599" />
          </div>
          <div data-id="element-2600">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2601">
              Campaign Code
            </label>
            <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 py-2 px-3 text-sm text-gray-500 cursor-not-allowed" value={formData.code} readOnly data-id="element-2602" />
          </div>
          <div className="md:col-span-2" data-id="element-2603">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2604">
              Description
            </label>
            <textarea rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="Internal description of the campaign..." value={formData.description} onChange={e => setFormData({
            ...formData,
            description: e.target.value
          })} data-id="element-2605" />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2606">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2607">
          <ClockIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2608" />
          Duration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2609">
          <div data-id="element-2610">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2611">
              Start Date & Time <span className="text-red-500" data-id="element-2612">*</span>
            </label>
            <input type="datetime-local" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" value={formData.startDate} onChange={e => setFormData({
            ...formData,
            startDate: e.target.value
          })} data-id="element-2613" />
          </div>
          <div data-id="element-2614">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2615">
              End Date & Time <span className="text-red-500" data-id="element-2616">*</span>
            </label>
            <input type="datetime-local" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" value={formData.endDate} onChange={e => setFormData({
            ...formData,
            endDate: e.target.value
          })} data-id="element-2617" />
          </div>
        </div>
      </div>

      {/* Applicable Days */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2618">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2619">
          <CalendarDaysIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2620" />
          Applicable Days
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-2621">
          Choose which days of the week this campaign will be active.
        </p>
        <div className="flex space-x-6 mb-4" data-id="element-2622">
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2623">
            <input type="radio" name="day_select" checked={formData.daySelectMode === 'all'} onChange={() => setFormData({
            ...formData,
            daySelectMode: 'all',
            applicableDays: []
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2624" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2625">
              All Days (7 days a week)
            </span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2626">
            <input type="radio" name="day_select" checked={formData.daySelectMode === 'specific'} onChange={() => setFormData({
            ...formData,
            daySelectMode: 'specific',
            applicableDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2627" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2628">
              Specific Days
            </span>
          </label>
        </div>

        {formData.daySelectMode === 'all' ? <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md flex items-start" data-id="element-2629">
            <InfoIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-2630" />
            <p className="text-sm text-blue-700 dark:text-blue-200" data-id="element-2631">
              Campaign will be active on all days of the week including
              weekends.
            </p>
          </div> : <div className="space-y-4" data-id="element-2632">
            {/* Quick select buttons */}
            <div className="flex flex-wrap gap-2" data-id="element-2633">
              <button type="button" onClick={selectWeekdaysOnly} className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-2634">
                Weekdays Only
              </button>
              <button type="button" onClick={selectWeekendsOnly} className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-2635">
                Weekends Only
              </button>
              <button type="button" onClick={selectAllDaysSpecific} className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-2636">
                Select All
              </button>
              <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            applicableDays: []
          }))} className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-2637">
                Clear All
              </button>
            </div>

            {/* Day toggle buttons */}
            <div className="grid grid-cols-7 gap-2" data-id="element-2638">
              {WEEKDAYS.map(day => {
            const isSelected = formData.applicableDays.includes(day.key);
            const isWeekend = day.key === 'Sat' || day.key === 'Sun';
            return <button key={day.key} type="button" onClick={() => toggleDay(day.key)} className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg border-2 transition-all text-center
                      ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 shadow-sm' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-2639">
                    <span className="text-sm font-bold" data-id="element-2640">{day.key}</span>
                    <span className={`text-[10px] mt-0.5 ${isSelected ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-2641">
                      {day.label.slice(0, 3)}
                    </span>
                    {isWeekend && <span className={`text-[9px] mt-1 px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-blue-100 dark:bg-blue-800/40 text-blue-600 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`} data-id="element-2642">
                        Weekend
                      </span>}
                  </button>;
          })}
            </div>

            {/* Selection summary */}
            {formData.applicableDays.length > 0 ? <div className="flex flex-wrap gap-1.5 pt-2" data-id="element-2643">
                {WEEKDAYS.filter(d => formData.applicableDays.includes(d.key)).map(day => <span key={day.key} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-2644">
                    {day.label}
                    <button onClick={() => toggleDay(day.key)} className="ml-1.5 hover:text-blue-900 dark:hover:text-blue-100" data-id="element-2645">
                      <XIcon className="h-3 w-3" data-id="element-2646" />
                    </button>
                  </span>)}
              </div> : <div className="flex items-center text-amber-600 dark:text-amber-400 text-sm mt-1" data-id="element-2647">
                <AlertCircleIcon className="h-4 w-4 mr-1.5" data-id="element-2648" />
                Please select at least one day for the campaign to be active.
              </div>}
          </div>}
      </div>
    </div>;
  const renderStep2 = () => <div className="space-y-6" data-id="element-2649">
      {/* Transaction Types */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2650">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2651">
          <CreditCardIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2652" />
          Transaction Types
        </h3>
        <div className="flex flex-wrap gap-4" data-id="element-2653">
          {['RAAST QR', 'Card', 'Online'].map(type => <label key={type} className="flex items-center space-x-2 cursor-pointer" data-id="element-2654">
              <input type="checkbox" checked={formData.transactionTypes.includes(type)} onChange={() => setFormData({
            ...formData,
            transactionTypes: toggleSelection(formData.transactionTypes, type)
          })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2655" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2656">
                {type}
              </span>
            </label>)}
        </div>
      </div>

      {/* Card Schemes - Conditional */}
      {formData.transactionTypes.includes('Card') && <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2657">
          <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2658">
            <CreditCardIcon className="h-5 w-5 mr-2 text-purple-500" data-id="element-2659" />
            Card Schemes
          </h3>
          <div className="flex flex-wrap gap-4" data-id="element-2660">
            {['PayPak', 'Visa', 'MasterCard', 'UnionPay'].map(scheme => <label key={scheme} className="flex items-center space-x-2 cursor-pointer" data-id="element-2661">
                <input type="checkbox" checked={formData.schemes.includes(scheme)} onChange={() => setFormData({
            ...formData,
            schemes: toggleSelection(formData.schemes, scheme)
          })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2662" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2663">
                  {scheme}
                </span>
              </label>)}
          </div>
        </div>}

      {/* Bank Selection */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2664">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2665">
          <BuildingIcon className="h-5 w-5 mr-2 text-green-500" data-id="element-2666" />
          Bank Selection
        </h3>
        <div className="flex space-x-6 mb-4" data-id="element-2667">
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2668">
            <input type="radio" name="bank_select" checked={formData.bankSelectMode === 'all'} onChange={() => setFormData({
            ...formData,
            bankSelectMode: 'all'
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2669" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2670">
              All Banks
            </span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2671">
            <input type="radio" name="bank_select" checked={formData.bankSelectMode === 'specific'} onChange={() => setFormData({
            ...formData,
            bankSelectMode: 'specific'
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2672" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2673">
              Specific Banks
            </span>
          </label>
        </div>

        {formData.bankSelectMode === 'specific' && <div className="space-y-3" data-id="element-2674">
            <div className="relative" data-id="element-2675">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2676" />
              <input type="text" placeholder="Search banks..." className="w-full pl-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" value={bankSearch} onChange={e => setBankSearch(e.target.value)} data-id="element-2677" />
            </div>
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2" data-id="element-2678">
              {MOCK_BANKS.filter(b => b.toLowerCase().includes(bankSearch.toLowerCase())).map(bank => <label key={bank} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer" data-id="element-2679">
                  <input type="checkbox" checked={formData.banks.includes(bank)} onChange={() => setFormData({
              ...formData,
              banks: toggleSelection(formData.banks, bank)
            })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2680" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2681">
                    {bank}
                  </span>
                </label>)}
            </div>
            <div className="flex flex-wrap gap-2 mt-2" data-id="element-2682">
              {formData.banks.map(bank => <span key={bank} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-2683">
                  {bank}
                  <button onClick={() => setFormData({
              ...formData,
              banks: formData.banks.filter(b => b !== bank)
            })} className="ml-1 hover:text-green-900" data-id="element-2684">
                    <XIcon className="h-3 w-3" data-id="element-2685" />
                  </button>
                </span>)}
            </div>
          </div>}
      </div>

      {/* BIN Filtering - Conditional */}
      {formData.transactionTypes.includes('Card') && <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2686">
          <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2687">
            <CreditCardIcon className="h-5 w-5 mr-2 text-orange-500" data-id="element-2688" />
            BIN Filtering
          </h3>
          <div className="flex space-x-6 mb-4" data-id="element-2689">
            <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2690">
              <input type="radio" name="bin_select" checked={formData.binSelectMode === 'all'} onChange={() => setFormData({
            ...formData,
            binSelectMode: 'all'
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2691" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2692">
                All BINs
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2693">
              <input type="radio" name="bin_select" checked={formData.binSelectMode === 'specific'} onChange={() => setFormData({
            ...formData,
            binSelectMode: 'specific'
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2694" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2695">
                Specific BINs
              </span>
            </label>
          </div>

          {formData.binSelectMode === 'specific' && <div className="space-y-3" data-id="element-2696">
              <div className="flex space-x-2" data-id="element-2697">
                <input type="text" placeholder="Enter 6-8 digit BIN" className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" value={binInput} onChange={e => setBinInput(e.target.value.replace(/\D/g, '').slice(0, 8))} data-id="element-2698" />
                <button onClick={addBin} disabled={binInput.length < 6} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50" data-id="element-2699">
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2" data-id="element-2700">
                {formData.bins.map(bin => <span key={bin} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" data-id="element-2701">
                    {bin}
                    <button onClick={() => removeBin(bin)} className="ml-1 hover:text-orange-900" data-id="element-2702">
                      <XIcon className="h-3 w-3" data-id="element-2703" />
                    </button>
                  </span>)}
              </div>
            </div>}
        </div>}

      {/* Merchant Selection */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2704">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2705">
          <UsersIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-2706" />
          Merchant Selection
        </h3>
        <div className="flex space-x-6 mb-4" data-id="element-2707">
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2708">
            <input type="radio" name="merchant_select" checked={formData.merchantSelectMode === 'all'} onChange={() => setFormData({
            ...formData,
            merchantSelectMode: 'all'
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2709" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2710">
              All Merchants
            </span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2711">
            <input type="radio" name="merchant_select" checked={formData.merchantSelectMode === 'specific'} onChange={() => setFormData({
            ...formData,
            merchantSelectMode: 'specific'
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2712" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2713">
              Specific Merchants
            </span>
          </label>
        </div>

        {formData.merchantSelectMode === 'all' ? <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md flex items-start" data-id="element-2714">
            <InfoIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-2715" />
            <p className="text-sm text-blue-700 dark:text-blue-200" data-id="element-2716">
              Campaign will apply to all registered merchants.
            </p>
          </div> : <div className="space-y-3" data-id="element-2717">
            <div className="relative" data-id="element-2718">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2719" />
              <input type="text" placeholder="Search merchants by ID or Name..." className="w-full pl-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" value={merchantSearch} onChange={e => setMerchantSearch(e.target.value)} data-id="element-2720" />
            </div>
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2" data-id="element-2721">
              {MOCK_MERCHANTS.filter(m => m.name.toLowerCase().includes(merchantSearch.toLowerCase())).map(merchant => <label key={merchant.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer" data-id="element-2722">
                  <input type="checkbox" checked={formData.merchants.includes(merchant.id)} onChange={() => setFormData({
              ...formData,
              merchants: toggleSelection(formData.merchants, merchant.id)
            })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2723" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2724">
                    {merchant.name} ({merchant.id})
                  </span>
                </label>)}
            </div>
            <div className="flex flex-wrap gap-2 mt-2" data-id="element-2725">
              {formData.merchants.map(merchantId => {
            const merchant = MOCK_MERCHANTS.find(m => m.id === merchantId);
            return <span key={merchantId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300" data-id="element-2726">
                    {merchant?.name}
                    <button onClick={() => setFormData({
                ...formData,
                merchants: formData.merchants.filter(id => id !== merchantId)
              })} className="ml-1 hover:text-indigo-900" data-id="element-2727">
                      <XIcon className="h-3 w-3" data-id="element-2728" />
                    </button>
                  </span>;
          })}
            </div>
          </div>}
      </div>

      {/* MCC Filtering */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2729">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2730">
          <TagIcon className="h-5 w-5 mr-2 text-teal-500" data-id="element-2731" />
          MCC (Merchant Category Code) Filtering
        </h3>
        <div className="flex space-x-6 mb-4" data-id="element-2732">
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2733">
            <input type="radio" name="mcc_select" checked={formData.mccSelectMode === 'all'} onChange={() => setFormData({
            ...formData,
            mccSelectMode: 'all'
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2734" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2735">
              All MCCs
            </span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2736">
            <input type="radio" name="mcc_select" checked={formData.mccSelectMode === 'specific'} onChange={() => setFormData({
            ...formData,
            mccSelectMode: 'specific'
          })} className="text-blue-600 focus:ring-blue-500" data-id="element-2737" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2738">
              Specific MCCs
            </span>
          </label>
        </div>

        {formData.mccSelectMode === 'all' ? <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md flex items-start" data-id="element-2739">
            <InfoIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-2740" />
            <p className="text-sm text-blue-700 dark:text-blue-200" data-id="element-2741">
              Campaign will apply to all merchant category codes.
            </p>
          </div> : <div className="space-y-3" data-id="element-2742">
            <div className="relative" data-id="element-2743">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2744" />
              <input type="text" placeholder="Search by MCC code or description..." className="w-full pl-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" value={mccSearch} onChange={e => setMccSearch(e.target.value)} data-id="element-2745" />
            </div>
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2" data-id="element-2746">
              {MOCK_MCCS.filter(m => m.code.includes(mccSearch) || m.description.toLowerCase().includes(mccSearch.toLowerCase())).map(mcc => <label key={mcc.code} className="flex items-center space-x-2 p-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer" data-id="element-2747">
                  <input type="checkbox" checked={formData.mccs.includes(mcc.code)} onChange={() => setFormData({
              ...formData,
              mccs: toggleSelection(formData.mccs, mcc.code)
            })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2748" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2749">
                    <span className="font-medium" data-id="element-2750">{mcc.code}</span>
                    <span className="text-gray-400 mx-1" data-id="element-2751">—</span>
                    {mcc.description}
                  </span>
                </label>)}
            </div>
            <div className="flex flex-wrap gap-2 mt-2" data-id="element-2752">
              {formData.mccs.map(code => {
            const mcc = MOCK_MCCS.find(m => m.code === code);
            return <span key={code} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300" data-id="element-2753">
                    {code} — {mcc?.description}
                    <button onClick={() => setFormData({
                ...formData,
                mccs: formData.mccs.filter(c => c !== code)
              })} className="ml-1 hover:text-teal-900" data-id="element-2754">
                      <XIcon className="h-3 w-3" data-id="element-2755" />
                    </button>
                  </span>;
          })}
            </div>
          </div>}
      </div>
    </div>;
  const renderStep3 = () => <div className="space-y-6" data-id="element-2756">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2757">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2758">
          <TagIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2759" />
          Discount Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2760">
          <div className="md:col-span-2" data-id="element-2761">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2762">
              Discount Type
            </label>
            <div className="flex space-x-6" data-id="element-2763">
              <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2764">
                <input type="radio" name="discountType" checked={formData.discountType === 'percentage'} onChange={() => setFormData({
                ...formData,
                discountType: 'percentage'
              })} className="text-blue-600 focus:ring-blue-500" data-id="element-2765" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2766">
                  Percentage (%)
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer" data-id="element-2767">
                <input type="radio" name="discountType" checked={formData.discountType === 'fixed'} onChange={() => setFormData({
                ...formData,
                discountType: 'fixed'
              })} className="text-blue-600 focus:ring-blue-500" data-id="element-2768" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2769">
                  Fixed Amount
                </span>
              </label>
            </div>
          </div>

          <div data-id="element-2770">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2771">
              {formData.discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}{' '}
              <span className="text-red-500" data-id="element-2772">*</span>
            </label>
            <div className="relative" data-id="element-2773">
              <input type="number" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="0" value={formData.discountValue} onChange={e => setFormData({
              ...formData,
              discountValue: Number(e.target.value)
            })} data-id="element-2774" />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2775">
                <span className="text-gray-500 sm:text-sm" data-id="element-2776">
                  {formData.discountType === 'percentage' ? '%' : 'PKR'}
                </span>
              </div>
            </div>
          </div>

          <div data-id="element-2777">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2778">
              Maximum Cap Amount <span className="text-red-500" data-id="element-2779">*</span>
            </label>
            <div className="relative" data-id="element-2780">
              <input type="number" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="0" value={formData.maxCap} onChange={e => setFormData({
              ...formData,
              maxCap: Number(e.target.value)
            })} data-id="element-2781" />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2782">
                <span className="text-gray-500 sm:text-sm" data-id="element-2783">PKR</span>
              </div>
            </div>
          </div>

          <div data-id="element-2784">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2785">
              Min. Transaction Amount <span className="text-red-500" data-id="element-2786">*</span>
            </label>
            <div className="relative" data-id="element-2787">
              <input type="number" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="0" value={formData.minTransaction} onChange={e => setFormData({
              ...formData,
              minTransaction: Number(e.target.value)
            })} data-id="element-2788" />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2789">
                <span className="text-gray-500 sm:text-sm" data-id="element-2790">PKR</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2791">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2792">
          <UsersIcon className="h-5 w-5 mr-2 text-green-500" data-id="element-2793" />
          Usage Limits
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2794">
          <div data-id="element-2795">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2796">
              Max Usage Per Customer
            </label>
            <input type="number" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="1" value={formData.usageLimitPerCustomer} onChange={e => setFormData({
            ...formData,
            usageLimitPerCustomer: Number(e.target.value)
          })} data-id="element-2797" />
          </div>

          <div data-id="element-2798">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2799">
              Max Discount Value Per Customer{' '}
              <span className="text-red-500" data-id="element-2800">*</span>
            </label>
            <div className="relative" data-id="element-2801">
              <input type="number" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="0" value={formData.maxDiscountPerCustomer} onChange={e => setFormData({
              ...formData,
              maxDiscountPerCustomer: Number(e.target.value)
            })} data-id="element-2802" />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2803">
                <span className="text-gray-500 sm:text-sm" data-id="element-2804">PKR</span>
              </div>
            </div>
          </div>

          <div data-id="element-2805">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2806">
              Usage Cycle
            </label>
            <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" value={formData.usageCycle} onChange={e => setFormData({
            ...formData,
            usageCycle: e.target.value
          })} data-id="element-2807">
              <option value="daily" data-id="element-2808">Daily</option>
              <option value="weekly" data-id="element-2809">Weekly</option>
              <option value="monthly" data-id="element-2810">Monthly</option>
              <option value="duration" data-id="element-2811">Campaign Duration</option>
            </select>
          </div>
        </div>
      </div>
    </div>;
  const renderStep4 = () => {
    const totalSplit = Object.values(formData.split).reduce((a, b) => a + b, 0);
    const isValid = totalSplit === 100;
    return <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 space-y-6" data-id="element-2812">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2813">
          <CreditCardIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2814" />
          Discount Cost Sharing
        </h3>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md flex items-start" data-id="element-2815">
          <InfoIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-2816" />
          <p className="text-sm text-blue-700 dark:text-blue-200" data-id="element-2817">
            Define how the discount cost will be shared among participants. The
            total must equal 100%.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2818">
          {Object.entries(formData.split).map(([key, value]) => <div key={key} data-id="element-2819">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize" data-id="element-2820">
                {key} Share (%)
              </label>
              <input type="number" min="0" max="100" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" value={value} onChange={e => setFormData({
            ...formData,
            split: {
              ...formData.split,
              [key]: Number(e.target.value)
            }
          })} data-id="element-2821" />
            </div>)}
        </div>

        <div className="mt-6" data-id="element-2822">
          <div className="flex justify-between text-sm font-medium mb-2" data-id="element-2823">
            <span data-id="element-2824">Total Allocation</span>
            <span className={isValid ? 'text-green-600' : 'text-red-600'} data-id="element-2825">
              {totalSplit}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden flex" data-id="element-2826">
            <div className="bg-blue-500 h-full transition-all duration-300" style={{
            width: `${formData.split.merchant}%`
          }} title="Merchant" data-id="element-2827" />
            <div className="bg-purple-500 h-full transition-all duration-300" style={{
            width: `${formData.split.institution}%`
          }} title="Institution" data-id="element-2828" />
            <div className="bg-green-500 h-full transition-all duration-300" style={{
            width: `${formData.split.scheme}%`
          }} title="Scheme" data-id="element-2829" />
            <div className="bg-orange-500 h-full transition-all duration-300" style={{
            width: `${formData.split.bank}%`
          }} title="Bank" data-id="element-2830" />
          </div>
          <div className="flex space-x-4 mt-2 text-xs text-gray-500" data-id="element-2831">
            <div className="flex items-center" data-id="element-2832">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-1" data-id="element-2833"></div>{' '}
              Merchant
            </div>
            <div className="flex items-center" data-id="element-2834">
              <div className="w-3 h-3 bg-purple-500 rounded-full mr-1" data-id="element-2835"></div>{' '}
              Institution
            </div>
            <div className="flex items-center" data-id="element-2836">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-1" data-id="element-2837"></div>{' '}
              Scheme
            </div>
            <div className="flex items-center" data-id="element-2838">
              <div className="w-3 h-3 bg-orange-500 rounded-full mr-1" data-id="element-2839"></div>{' '}
              Bank
            </div>
          </div>
        </div>

        {!isValid && <div className="flex items-center text-red-600 text-sm mt-2" data-id="element-2840">
            <AlertCircleIcon className="h-4 w-4 mr-1" data-id="element-2841" />
            Total allocation must be exactly 100%
          </div>}
      </div>;
  };
  const renderStep5 = () => {
    const budgetEditable = !budgetLocked || isRevisingBudget;
    return <div className="space-y-6" data-id="element-2842">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2843">
          <div className="flex items-center justify-between mb-4" data-id="element-2844">
            <h3 className="text-lg font-medium flex items-center" data-id="element-2845">
              <WalletIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2846" />
              Campaign Budget
            </h3>
            {budgetLocked && !isRevisingBudget && <button onClick={() => setIsRevisingBudget(true)} className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-900/30 transition-colors" data-id="element-2847">
                <EditIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-2848" />
                Revise Budget
              </button>}
          </div>

          {isRevisingBudget && <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-md flex items-start mb-6 border border-amber-200 dark:border-amber-800" data-id="element-2849">
              <AlertCircleIcon className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-2850" />
              <div className="flex-1" data-id="element-2851">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2" data-id="element-2852">
                  Budget Revision Mode
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-3" data-id="element-2853">
                  You are modifying the previously approved budget. Please
                  provide a reason for the revision.
                </p>
                <textarea rows={2} className="w-full rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-amber-500 focus:border-amber-500" placeholder="Reason for budget revision..." value={budgetRevisionReason} onChange={e => setBudgetRevisionReason(e.target.value)} data-id="element-2854" />
              </div>
            </div>}

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md flex items-start mb-6" data-id="element-2855">
            <InfoIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-2856" />
            <p className="text-sm text-blue-700 dark:text-blue-200" data-id="element-2857">
              Define the budget limits for this campaign. Daily and monthly
              budgets act as spending caps within the total campaign budget.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2858">
            <div data-id="element-2859">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2860">
                Total Campaign Budget <span className="text-red-500" data-id="element-2861">*</span>
              </label>
              <div className="relative" data-id="element-2862">
                <input type="number" className={`w-full rounded-md border py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 ${budgetEditable ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800' : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'}`} placeholder="0" value={formData.totalBudget || ''} onChange={e => setFormData({
                ...formData,
                totalBudget: Number(e.target.value)
              })} disabled={!budgetEditable} data-id="element-2863" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2864">
                  <span className="text-gray-500 sm:text-sm" data-id="element-2865">PKR</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1" data-id="element-2866">
                Maximum spend for the entire campaign lifecycle
              </p>
            </div>

            <div data-id="element-2867">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2868">
                Daily Budget <span className="text-red-500" data-id="element-2869">*</span>
              </label>
              <div className="relative" data-id="element-2870">
                <input type="number" className={`w-full rounded-md border py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 ${budgetEditable ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800' : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'}`} placeholder="0" value={formData.dailyBudget || ''} onChange={e => setFormData({
                ...formData,
                dailyBudget: Number(e.target.value)
              })} disabled={!budgetEditable} data-id="element-2871" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2872">
                  <span className="text-gray-500 sm:text-sm" data-id="element-2873">PKR</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1" data-id="element-2874">
                Maximum spend per day
              </p>
            </div>

            <div data-id="element-2875">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2876">
                Monthly Budget <span className="text-red-500" data-id="element-2877">*</span>
              </label>
              <div className="relative" data-id="element-2878">
                <input type="number" className={`w-full rounded-md border py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 ${budgetEditable ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800' : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'}`} placeholder="0" value={formData.monthlyBudget || ''} onChange={e => setFormData({
                ...formData,
                monthlyBudget: Number(e.target.value)
              })} disabled={!budgetEditable} data-id="element-2879" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2880">
                  <span className="text-gray-500 sm:text-sm" data-id="element-2881">PKR</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1" data-id="element-2882">
                Maximum spend per calendar month
              </p>
            </div>
          </div>

          {/* Budget validation warnings */}
          {formData.dailyBudget > 0 && formData.monthlyBudget > 0 && formData.dailyBudget * 30 > formData.monthlyBudget && <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md flex items-start" data-id="element-2883">
                <AlertCircleIcon className="h-4 w-4 text-amber-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-2884" />
                <p className="text-xs text-amber-700 dark:text-amber-300" data-id="element-2885">
                  Daily budget × 30 days (
                  {(formData.dailyBudget * 30).toLocaleString()} PKR) exceeds
                  the monthly budget. The monthly cap will take precedence.
                </p>
              </div>}

          {formData.monthlyBudget > 0 && formData.totalBudget > 0 && formData.monthlyBudget > formData.totalBudget && <div className="mt-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-md flex items-start" data-id="element-2886">
                <AlertCircleIcon className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-2887" />
                <p className="text-xs text-red-700 dark:text-red-300" data-id="element-2888">
                  Monthly budget cannot exceed the total campaign budget.
                </p>
              </div>}
        </div>

        {/* Budget Summary Card */}
        {(formData.totalBudget > 0 || formData.dailyBudget > 0 || formData.monthlyBudget > 0) && <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2889">
            <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2890">
              <InfoIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-2891" />
              Budget Overview
            </h3>
            <div className="grid grid-cols-3 gap-4" data-id="element-2892">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-2893">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2894">
                  Total Lifecycle
                </p>
                <p className="text-xl font-bold text-blue-700 dark:text-blue-300" data-id="element-2895">
                  {formData.totalBudget > 0 ? `${formData.totalBudget.toLocaleString()}` : '—'}
                </p>
                <p className="text-xs text-gray-400" data-id="element-2896">PKR</p>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg" data-id="element-2897">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2898">
                  Daily Cap
                </p>
                <p className="text-xl font-bold text-green-700 dark:text-green-300" data-id="element-2899">
                  {formData.dailyBudget > 0 ? `${formData.dailyBudget.toLocaleString()}` : '—'}
                </p>
                <p className="text-xs text-gray-400" data-id="element-2900">PKR</p>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg" data-id="element-2901">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2902">
                  Monthly Cap
                </p>
                <p className="text-xl font-bold text-purple-700 dark:text-purple-300" data-id="element-2903">
                  {formData.monthlyBudget > 0 ? `${formData.monthlyBudget.toLocaleString()}` : '—'}
                </p>
                <p className="text-xs text-gray-400" data-id="element-2904">PKR</p>
              </div>
            </div>
          </div>}

        {isRevisingBudget && <div className="flex justify-end space-x-3" data-id="element-2905">
            <button onClick={() => {
          setIsRevisingBudget(false);
          setBudgetRevisionReason('');
        }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50" data-id="element-2906">
              Cancel Revision
            </button>
            <button onClick={() => {
          setIsRevisingBudget(false);
          setBudgetLocked(true);
        }} disabled={!budgetRevisionReason.trim()} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 flex items-center" data-id="element-2907">
              <CheckIcon className="h-4 w-4 mr-1.5" data-id="element-2908" />
              Confirm Revision
            </button>
          </div>}
      </div>;
  };
  const renderStep6 = () => <div className="space-y-6" data-id="element-2909">
      {/* Campaign Info */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2910">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2911">
          <InfoIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-2912" />
          Campaign Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-2913">
          <div data-id="element-2914">
            <span className="text-gray-500 block" data-id="element-2915">Name</span>
            <span className="font-medium" data-id="element-2916">{formData.name || 'Untitled'}</span>
          </div>
          <div data-id="element-2917">
            <span className="text-gray-500 block" data-id="element-2918">Code</span>
            <span className="font-medium" data-id="element-2919">{formData.code}</span>
          </div>
          <div data-id="element-2920">
            <span className="text-gray-500 block" data-id="element-2921">Duration</span>
            <span className="font-medium" data-id="element-2922">
              {formData.startDate ? new Date(formData.startDate).toLocaleDateString() : 'N/A'}{' '}
              -{' '}
              {formData.endDate ? new Date(formData.endDate).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          <div data-id="element-2923">
            <span className="text-gray-500 block" data-id="element-2924">Applicable Days</span>
            {formData.daySelectMode === 'all' ? <span className="font-medium" data-id="element-2925">All Days (7 days a week)</span> : formData.applicableDays.length > 0 ? <div className="flex flex-wrap gap-1 mt-1" data-id="element-2926">
                {WEEKDAYS.filter(d => formData.applicableDays.includes(d.key)).map(day => <span key={day.key} className="px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs font-medium" data-id="element-2927">
                    {day.label}
                  </span>)}
              </div> : <span className="font-medium text-amber-600" data-id="element-2928">
                No days selected
              </span>}
          </div>
          <div className="md:col-span-2" data-id="element-2929">
            <span className="text-gray-500 block" data-id="element-2930">Description</span>
            <span className="font-medium" data-id="element-2931">
              {formData.description || 'No description provided'}
            </span>
          </div>
        </div>
      </div>

      {/* Targeting Rules */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2932">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2933">
          <UsersIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-2934" />
          Targeting Rules
        </h3>
        <div className="space-y-4 text-sm" data-id="element-2935">
          <div data-id="element-2936">
            <span className="text-gray-500 block mb-1" data-id="element-2937">Transaction Types</span>
            <div className="flex flex-wrap gap-2" data-id="element-2938">
              {formData.transactionTypes.map(t => <span key={t} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs font-medium" data-id="element-2939">
                  {t}
                </span>)}
            </div>
          </div>
          {formData.transactionTypes.includes('Card') && <div data-id="element-2940">
              <span className="text-gray-500 block mb-1" data-id="element-2941">Schemes</span>
              <div className="flex flex-wrap gap-2" data-id="element-2942">
                {formData.schemes.map(s => <span key={s} className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded text-xs font-medium" data-id="element-2943">
                    {s}
                  </span>)}
              </div>
            </div>}
          <div data-id="element-2944">
            <span className="text-gray-500 block mb-1" data-id="element-2945">Banks</span>
            <span className="font-medium" data-id="element-2946">
              {formData.bankSelectMode === 'all' ? 'All Banks' : `${formData.banks.length} Specific Banks Selected`}
            </span>
          </div>
          <div data-id="element-2947">
            <span className="text-gray-500 block mb-1" data-id="element-2948">Merchants</span>
            <span className="font-medium" data-id="element-2949">
              {formData.merchantSelectMode === 'all' ? 'All Merchants' : `${formData.merchants.length} Specific Merchants Selected`}
            </span>
          </div>
          <div data-id="element-2950">
            <span className="text-gray-500 block mb-1" data-id="element-2951">MCC Codes</span>
            {formData.mccSelectMode === 'all' ? <span className="font-medium" data-id="element-2952">All MCCs</span> : <div className="flex flex-wrap gap-1 mt-1" data-id="element-2953">
                {formData.mccs.length > 0 ? formData.mccs.map(code => {
              const mcc = MOCK_MCCS.find(m => m.code === code);
              return <span key={code} className="px-2 py-0.5 bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 rounded text-xs font-medium" data-id="element-2954">
                        {code} — {mcc?.description}
                      </span>;
            }) : <span className="font-medium text-gray-400" data-id="element-2955">
                    None selected
                  </span>}
              </div>}
          </div>
          {formData.transactionTypes.includes('Card') && <div data-id="element-2956">
              <span className="text-gray-500 block mb-1" data-id="element-2957">BINs</span>
              <span className="font-medium" data-id="element-2958">
                {formData.binSelectMode === 'all' ? 'All BINs' : formData.bins.length > 0 ? formData.bins.join(', ') : 'None selected'}
              </span>
            </div>}
        </div>
      </div>

      {/* Discount Structure */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2959">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2960">
          <TagIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-2961" />
          Discount Structure
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-2962">
          <div data-id="element-2963">
            <span className="text-gray-500 block" data-id="element-2964">Type</span>
            <span className="font-medium capitalize" data-id="element-2965">
              {formData.discountType}
            </span>
          </div>
          <div data-id="element-2966">
            <span className="text-gray-500 block" data-id="element-2967">Value</span>
            <span className="font-medium" data-id="element-2968">
              {formData.discountValue}{' '}
              {formData.discountType === 'percentage' ? '%' : 'PKR'}
            </span>
          </div>
          <div data-id="element-2969">
            <span className="text-gray-500 block" data-id="element-2970">Max Cap</span>
            <span className="font-medium" data-id="element-2971">{formData.maxCap} PKR</span>
          </div>
          <div data-id="element-2972">
            <span className="text-gray-500 block" data-id="element-2973">Min Transaction</span>
            <span className="font-medium" data-id="element-2974">{formData.minTransaction} PKR</span>
          </div>
          <div data-id="element-2975">
            <span className="text-gray-500 block" data-id="element-2976">Usage Limit</span>
            <span className="font-medium" data-id="element-2977">
              {formData.usageLimitPerCustomer} per customer (
              {formData.usageCycle})
            </span>
          </div>
          <div data-id="element-2978">
            <span className="text-gray-500 block" data-id="element-2979">Max Discount Limit</span>
            <span className="font-medium" data-id="element-2980">
              {formData.maxDiscountPerCustomer} PKR per customer
            </span>
          </div>
        </div>
      </div>

      {/* Funding Split */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2981">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-2982">
          <CreditCardIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-2983" />
          Funding Split
        </h3>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden flex mb-4" data-id="element-2984">
          <div className="bg-blue-500 h-full" style={{
          width: `${formData.split.merchant}%`
        }} data-id="element-2985" />
          <div className="bg-purple-500 h-full" style={{
          width: `${formData.split.institution}%`
        }} data-id="element-2986" />
          <div className="bg-green-500 h-full" style={{
          width: `${formData.split.scheme}%`
        }} data-id="element-2987" />
          <div className="bg-orange-500 h-full" style={{
          width: `${formData.split.bank}%`
        }} data-id="element-2988" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="element-2989">
          <div className="flex items-center" data-id="element-2990">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2" data-id="element-2991"></div>
            <div data-id="element-2992">
              <span className="text-gray-500 block text-xs" data-id="element-2993">Merchant</span>
              <span className="font-medium" data-id="element-2994">{formData.split.merchant}%</span>
            </div>
          </div>
          <div className="flex items-center" data-id="element-2995">
            <div className="w-3 h-3 bg-purple-500 rounded-full mr-2" data-id="element-2996"></div>
            <div data-id="element-2997">
              <span className="text-gray-500 block text-xs" data-id="element-2998">Institution</span>
              <span className="font-medium" data-id="element-2999">{formData.split.institution}%</span>
            </div>
          </div>
          <div className="flex items-center" data-id="element-3000">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2" data-id="element-3001"></div>
            <div data-id="element-3002">
              <span className="text-gray-500 block text-xs" data-id="element-3003">Scheme</span>
              <span className="font-medium" data-id="element-3004">{formData.split.scheme}%</span>
            </div>
          </div>
          <div className="flex items-center" data-id="element-3005">
            <div className="w-3 h-3 bg-orange-500 rounded-full mr-2" data-id="element-3006"></div>
            <div data-id="element-3007">
              <span className="text-gray-500 block text-xs" data-id="element-3008">Bank</span>
              <span className="font-medium" data-id="element-3009">{formData.split.bank}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Budget */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3010">
        <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-3011">
          <WalletIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-3012" />
          Budget Allocation
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-id="element-3013">
          <div data-id="element-3014">
            <span className="text-gray-500 block" data-id="element-3015">Total Campaign Budget</span>
            <span className="font-medium" data-id="element-3016">
              {formData.totalBudget > 0 ? `${formData.totalBudget.toLocaleString()} PKR` : 'Not set'}
            </span>
          </div>
          <div data-id="element-3017">
            <span className="text-gray-500 block" data-id="element-3018">Daily Budget</span>
            <span className="font-medium" data-id="element-3019">
              {formData.dailyBudget > 0 ? `${formData.dailyBudget.toLocaleString()} PKR` : 'Not set'}
            </span>
          </div>
          <div data-id="element-3020">
            <span className="text-gray-500 block" data-id="element-3021">Monthly Budget</span>
            <span className="font-medium" data-id="element-3022">
              {formData.monthlyBudget > 0 ? `${formData.monthlyBudget.toLocaleString()} PKR` : 'Not set'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-start space-x-3 p-4 border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 rounded-md" data-id="element-3023">
        <input type="checkbox" className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-3024" />
        <p className="text-sm text-blue-800 dark:text-blue-200" data-id="element-3025">
          I confirm that the campaign configuration has been reviewed and
          complies with the internal pricing policy. Submitting this will send
          the campaign for approval.
        </p>
      </div>
    </div>;
  const ClockIcon = ({
    className
  }: {
    className?: string;
  }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-3026">
      <circle cx="12" cy="12" r="10" data-id="element-3027" />
      <polyline points="12 6 12 12 16 14" data-id="element-3028" />
    </svg>;
  return <div className="w-full" data-id="element-3029">
      <div className="mb-8" data-id="element-3030">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3031">
          Create New Campaign
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3032">
          Configure discount rules, targeting, and funding split
        </p>
      </div>

      {/* Progress Steps — Modern Header Design */}
      <div className="mb-8" data-id="element-3033">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-1.5 shadow-sm" data-id="element-3034">
          <div className="flex items-center" data-id="element-3035">
            {STEPS.map((step, index) => <Fragment key={step.id} data-id="element-3036">
                <button onClick={() => {
              if (step.id < currentStep) setCurrentStep(step.id);
            }} className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-3 rounded-lg text-sm font-medium transition-all duration-200
                    ${currentStep === step.id ? 'bg-blue-600 text-white shadow-md' : currentStep > step.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30' : 'text-gray-400 dark:text-gray-500 cursor-default'}`} data-id="element-3037">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                    ${currentStep === step.id ? 'bg-white/20 text-white' : currentStep > step.id ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`} data-id="element-3038">
                    {currentStep > step.id ? <CheckIcon className="h-3.5 w-3.5" data-id="element-3039" /> : step.id}
                  </span>
                  <span className="hidden md:inline whitespace-nowrap" data-id="element-3040">
                    {step.name}
                  </span>
                </button>
                {index < STEPS.length - 1 && <div className={`w-6 h-0.5 flex-shrink-0 transition-colors duration-200 ${currentStep > step.id ? 'bg-blue-400' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-3041" />}
              </Fragment>)}
          </div>
        </div>
      </div>

      <Card data-id="element-3042">
        <div className="min-h-[400px]" data-id="element-3043">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
          {currentStep === 6 && renderStep6()}
        </div>

        <div className="flex justify-between mt-8 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3044">
          <button onClick={handleBack} disabled={currentStep === 1} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center" data-id="element-3045">
            <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-3046" />
            Back
          </button>

          {currentStep < STEPS.length ? <button onClick={handleNext} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center" data-id="element-3047">
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-3048" />
            </button> : <button onClick={handleSubmit} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center" data-id="element-3049">
              <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-3050" />
              Submit for Approval
            </button>}
        </div>
      </Card>
    </div>;
};
export default CampaignForm;